import { Popover, Portal } from "@headlessui/react";
import { useContext, useMemo } from "react";
import { GetComplianceProgramForReportQuery } from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import QuestionCalibrationSection from "@apps/assessments/components/calibration/question-calibration-section";
import useAssessmentCalibrationData from "@apps/assessments/components/calibration/use-assessment-calibration-data";
import PerformanceAssessmentReportContext from "@apps/reporting/context/performance-assessment-report-context";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Loading from "@components/loading/loading";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";

import { PerformanceAssessmentTableQuestion } from "./performance-assessment-table";

const CalibrationCellPopover = ({
  assessmentTemplate,
  target,
  canCalibrate,
  canSuggestCalibration,
  styles,
  attributes,
  questionLabels,
  question,
  setElement,
}: {
  assessmentTemplate: NonNullable<
    NonNullable<
      GetComplianceProgramForReportQuery["complianceProgram"]
    >["performanceAssessmentTemplate"]
  >;
  target: BasicUser;
  canCalibrate: boolean;
  canSuggestCalibration: boolean;
  styles: React.CSSProperties;
  attributes: React.HTMLAttributes<HTMLDivElement>;
  question: PerformanceAssessmentTableQuestion;
  questionLabels: Record<string, string>;
  setElement: (element: HTMLDivElement) => void;
}) => {
  const { complianceProgramId } = useContext(
    PerformanceAssessmentReportContext,
  );
  const { allCalibrations, loading: isLoadingCalibrations } =
    useAssessmentCalibrationData({
      complianceProgramId: complianceProgramId,
      templateId: assessmentTemplate.id,
      targetId: target.id,
    });

  const answerCalibration = useMemo(() => {
    return allCalibrations.filter(
      (calibration) => calibration.question?.id === question.id,
    );
  }, [allCalibrations, question]);

  return (
    <Portal>
      <Popover.Panel
        className="absolute left-0 z-dropdown bg-white rounded-lg border shadow-lg min-w-112 max-w-2xl max-h-[65dvh] overflow-y-scroll"
        ref={setElement}
        style={styles}
        aria-label="Assessment answer cell popover"
        {...attributes}
      >
        {({ close }) => (
          <>
            <div className="flex items-center py-6 px-6">
              <div className="tf-header-base-dark mr-2 flex-1">
                {question.title}
              </div>
              {isLoadingCalibrations && (
                <Loading mini size={4} className="mr-2" />
              )}
              <Button
                theme={buttonTheme.iconGray}
                icon={TopicflowIconMap.close}
                onClick={() => close()}
              />
            </div>

            <div className="border-t p-6">
              <QuestionCalibrationSection
                complianceProgramId={complianceProgramId}
                templateId={assessmentTemplate.id}
                questionId={question.id}
                targetId={target.id}
                calibrations={answerCalibration}
                canCalibrate={canCalibrate}
                canSuggestCalibration={canSuggestCalibration}
                expanded
                isLoadingCalibrations={isLoadingCalibrations}
                questionLabels={questionLabels}
              />
            </div>
          </>
        )}
      </Popover.Panel>
    </Portal>
  );
};

export default CalibrationCellPopover;
